import type { InputTableQuerySpec, InputWorkflowViewParamInput } from "@selector/shared/graphql";
import { clsx } from "clsx";
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ActionButton, Badge, Description, FormError, HStack, Label, Spacer, VStack } from "widgets";
import formStyle from "widgets/form.module.css";
import { useDataAttributes } from "widgets/hooks";

import type { Contig } from "@/graphql.ts";
import { VariantsModal } from "@/modules/workflow/components/parameters/VariantsModal.tsx";
import { VariantsTile } from "@/modules/workflow/components/parameters/VariantsTile.tsx";
import {
  useGetRefGenomeIdsFromGroupLazyQuery,
  useGetTableValueParamForViewIdLazyQuery,
  useSaveTableViewMutation,
} from "@/modules/workflow/graphql/coordinate-lists.gql.ts";
import { useWorkflowEditorStore } from "@/modules/workflow/stores/workflow-editor-store.ts";
import type { TableParamType } from "@/modules/workflow/utils/types.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { DATATABLE_ID_TYPE, DATATABLE_PARENT_TYPE, ENTITY_TYPE, INVALID_VIEW_PATH, VIEW } from "@/settings/settings.ts";
import { VariantMutantGenomesSelectionActionButton } from "@/ui-modules/genome-viewer/VariantMutantGenomesSelectionForm.tsx";

interface VariantsWidgetProps {
  availableGenomesIds: number[];
  label: string;
  description: string | null;
  optional?: boolean;
  onViewSaved: (viewId: number | null) => void;
  error?: Error;
  extraAction?: ReactElement;
  /** viewId */
  currentValue?: TableParamType;
  isPlaybookExecutionContext?: boolean;
  isDisabled?: boolean;
}

interface PersistVariantsSelectionOptions {
  genomeId: number | null;
  groupId: number | null;
  restrictionVariantGenomesIds: number[];
  selectedVariantsIds: number[];
  isOrganismTable: boolean | null;
  allowEmptySelection?: boolean;
  rowFilter?: string;
}

type SelectionMode = "organism" | "group";

interface SelectionContextState {
  mode: SelectionMode;
  genomeId: number | null;
  groupId: number | null;
}

interface GroupSelectionOptions {
  groupId: number | null;
  genomeId: number | null;
  preserveGenome?: boolean;
}

const useSelectionContext = () => {
  const [selectionContext, setSelectionContext] = useState<SelectionContextState>({
    mode: "organism",
    genomeId: null,
    groupId: null,
  });

  const selectOrganism = useCallback((genomeId: number | null) => {
    setSelectionContext({ mode: "organism", genomeId, groupId: null });
  }, []);

  const selectGroup = useCallback(({ groupId, genomeId, preserveGenome }: GroupSelectionOptions) => {
    setSelectionContext((previous) => ({
      mode: "group",
      groupId,
      genomeId: preserveGenome ? previous.genomeId : genomeId,
    }));
  }, []);

  const updateGenome = useCallback((genomeId: number | null) => {
    setSelectionContext((previous) => ({ ...previous, genomeId }));
  }, []);

  return {
    selectionContext,
    selectOrganism,
    selectGroup,
    updateGenome,
  };
};

// Component can only be called from WorkflowEditor because of store usage
export const VariantsWidget = (props: VariantsWidgetProps) => {
  const workflowEventId = useWorkflowEditorStore((state) => state.workflowEvent.id);
  return <VariantsWidgetCore {...props} workflowEventId={workflowEventId} />;
};

interface VariantsWidgetCoreProps extends VariantsWidgetProps {
  workflowEventId: number | undefined;
}

// eslint-disable-next-line complexity
export const VariantsWidgetCore = ({
  label,
  description,
  optional,
  error,
  availableGenomesIds,
  workflowEventId,
  onViewSaved,
  currentValue,
  isPlaybookExecutionContext,
  isDisabled,
  extraAction,
  ...props
}: VariantsWidgetCoreProps) => {
  const dataAttributes = useDataAttributes(props);
  const [showModal, setShowModal] = useState(false);

  // Query - Load the view
  const [getTableValueParamForViewId, { loading: isLoadingView }] = useGetTableValueParamForViewIdLazyQuery();
  const [getRefGenomeIdsFromGroup, { loading: isLoadingGroupGenomeId }] = useGetRefGenomeIdsFromGroupLazyQuery();
  const [saveVariantsView, { loading: isSavingVariantGenomes }] = useSaveTableViewMutation();
  const [variantSelectionError, setVariantSelectionError] = useState<string | null>(null);

  const [noGenomesAvailable, setNoGenomesAvailable] = useState(availableGenomesIds.length === 0);
  const { selectionContext, selectOrganism, selectGroup, updateGenome } = useSelectionContext();
  const isOrganismTable = selectionContext.mode === "organism";
  const selectedGenomeId = selectionContext.genomeId;
  const selectedGroupId = selectionContext.groupId;
  const resetVariantSelectionError = useCallback(() => {
    setVariantSelectionError(null);
  }, []);
  const applySelectionContext = useCallback(
    (
      mode: SelectionMode,
      target: { genomeId: number | null; groupId: number | null },
      options?: { preserveGenome?: boolean },
    ) => {
      resetVariantSelectionError();
      if (mode === "organism") {
        selectOrganism(target.genomeId);
        return;
      }

      selectGroup({
        groupId: target.groupId,
        genomeId: target.genomeId,
        preserveGenome: options?.preserveGenome,
      });
    },
    [resetVariantSelectionError, selectGroup, selectOrganism],
  );

  const [restrictionVariantGenomesIds, setRestrictionVariantGenomesIds] = useState<number[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<number[] | null>(null);
  const [viewRowFilter, setViewRowFilter] = useState<string | undefined>(undefined);
  const [restrictionRegion, setRestrictionRegion] = useState<
    | {
        contigId: number;
        range: {
          start: number;
          stop: number;
        } | null;
      }
    | null
    | undefined
  >(null);

  // Track view id changes so we can reset when the view is cleared.
  const normalizedViewId = currentValue ?? null;
  const previousViewId = useRef<number | null>(normalizedViewId);
  const [currentViewId, setCurrentViewId] = useState<number | null>(normalizedViewId);
  const availableGenomesIdsRef = useRef<number[]>(availableGenomesIds);

  useEffect(() => {
    if (normalizedViewId !== previousViewId.current) {
      previousViewId.current = normalizedViewId;
      setCurrentViewId(normalizedViewId);
    }
  }, [normalizedViewId]);

  useEffect(() => {
    availableGenomesIdsRef.current = availableGenomesIds;
  }, [availableGenomesIds]);

  useEffect(() => {
    if (isDisabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowModal(false);
    }
  }, [isDisabled]);

  useEffect(() => {
    if (currentViewId) {
      getTableValueParamForViewId({ variables: { viewId: currentViewId } }).then(({ data, error }) => {
        if (error) {
          setVariantSelectionError(error.message);
          return;
        }

        const tableValue = data?.workflow.getTableValueParamForViewId.value;
        const tableQuerySpec = tableValue?.state?.tableView?.tableQuerySpec;
        const restrictionRegion = tableQuerySpec?.restrictions?.region;
        const rowFilter = tableQuerySpec?.rowFilter;
        const tableUsesOrganism =
          tableQuerySpec?.idType === DATATABLE_ID_TYPE.ENTITY && tableQuerySpec.queryType === ENTITY_TYPE.ORGANISM;
        const genomeId = tableUsesOrganism ? (tableQuerySpec?.id ?? null) : null;
        const groupId = tableUsesOrganism ? null : (tableQuerySpec?.id ?? null);
        const restrictionVariantGenomesIds =
          tableQuerySpec?.restrictions?.variantGenomeIds?.filter((id) => typeof id === "number") ?? [];
        const selectedVariantsIds =
          tableValue?.state?.tableView?.selectedIds?.filter((id) => typeof id === "number") ?? null;

        applySelectionContext(
          tableUsesOrganism ? "organism" : "group",
          {
            genomeId: tableUsesOrganism ? (genomeId ?? null) : null,
            groupId: tableUsesOrganism ? null : (groupId ?? null),
          },
          { preserveGenome: !tableUsesOrganism },
        );

        setSelectedVariants(selectedVariantsIds);

        if (genomeId && availableGenomesIdsRef.current.includes(genomeId)) {
          setRestrictionVariantGenomesIds(restrictionVariantGenomesIds);
          setRestrictionRegion(restrictionRegion);
          setViewRowFilter(rowFilter ?? undefined);
        }
      });
    } else {
      // reset
      applySelectionContext("organism", { genomeId: null, groupId: null });
      setRestrictionVariantGenomesIds([]);
      setSelectedVariants(null);
      setRestrictionRegion(null);
      setViewRowFilter(undefined);
      onViewSaved(null);
    }
  }, [applySelectionContext, getTableValueParamForViewId, currentViewId, onViewSaved]);

  useEffect(() => {
    if (selectedGroupId == null) {
      return;
    }

    getRefGenomeIdsFromGroup({ variables: { groupId: selectedGroupId } }).then(({ data, error }) => {
      if (error) {
        setVariantSelectionError(error.message);
        return;
      }

      const refGenome = data?.group?.analysisGroup?.refGenomes?.[0]?.id;
      if (refGenome) {
        availableGenomesIdsRef.current = [refGenome];
        resetVariantSelectionError();
        updateGenome(refGenome);
        setNoGenomesAvailable(false);
      }
    });
  }, [getRefGenomeIdsFromGroup, resetVariantSelectionError, selectedGroupId, updateGenome]);

  useEffect(() => {
    if (!selectedGenomeId || !isOrganismTable) {
      return;
    }

    if (!availableGenomesIdsRef.current.includes(selectedGenomeId)) {
      // genomes in the upper tree probably changed, so we force reset
      onViewSaved(null);
      applySelectionContext("organism", { genomeId: null, groupId: null });
      setRestrictionVariantGenomesIds([]);
      setSelectedVariants(null);
      setViewRowFilter(undefined);
    }
  }, [applySelectionContext, availableGenomesIds, isOrganismTable, onViewSaved, selectedGenomeId]);

  const autoSelectedGenomeId =
    isPlaybookExecutionContext && isOrganismTable && !selectedGenomeId && availableGenomesIds.length > 0
      ? availableGenomesIds[0]
      : null;
  const activeGenomeId = selectedGenomeId ?? autoSelectedGenomeId;

  const persistVariantsSelection = useCallback(
    async ({
      genomeId,
      groupId,
      restrictionVariantGenomesIds: nextRestrictions,
      selectedVariantsIds: nextSelectedIds,
      isOrganismTable: selectionIsOrganismTable,
      allowEmptySelection = false,
      rowFilter,
    }: PersistVariantsSelectionOptions) => {
      if (isDisabled) {
        return;
      }

      if (selectionIsOrganismTable && genomeId == null) {
        throw new Error("Please select a genome first");
      }

      if (selectionIsOrganismTable === false && groupId == null) {
        throw new Error("Unable to determine group context");
      }

      if (selectionIsOrganismTable == null) {
        throw new Error("Unable to determine table type");
      }

      const selectionMode: SelectionMode = selectionIsOrganismTable ? "organism" : "group";
      const selectionTarget = {
        genomeId,
        groupId: selectionIsOrganismTable ? null : groupId,
      };
      const preserveGenome = !selectionIsOrganismTable;

      if (!allowEmptySelection && nextSelectedIds.length === 0) {
        onViewSaved(null);
        applySelectionContext(selectionMode, selectionTarget, { preserveGenome });
        setRestrictionVariantGenomesIds([]);
        setSelectedVariants(null);
        setViewRowFilter(undefined);
        return;
      }

      const tableQuerySpec: InputTableQuerySpec = selectionIsOrganismTable
        ? {
            idType: DATATABLE_ID_TYPE.ENTITY,
            queryType: DATATABLE_PARENT_TYPE.ORGANISM,
            resultType: ENTITY_TYPE.VCF_VARIANT,
            id: genomeId!,
            restrictions: { variantGenomeIds: nextRestrictions, region: restrictionRegion ?? undefined },
            rowFilter,
          }
        : {
            idType: DATATABLE_ID_TYPE.GROUP,
            queryType: null,
            resultType: ENTITY_TYPE.VCF_VARIANT,
            id: groupId!,
            restrictions: { variantGenomeIds: nextRestrictions, region: restrictionRegion ?? undefined },
            rowFilter,
          };

      const viewInput: InputWorkflowViewParamInput = {
        viewLabel: VIEW.TABLE_VIEW.title,
        viewType: VIEW.TABLE_VIEW.type,
        path: INVALID_VIEW_PATH,
        workflowEventId,
        state: {
          tableView: {
            tableQuerySpec,
            selectedIds: nextSelectedIds,
          },
        },
      };

      const { data } = await saveVariantsView({ variables: { viewInput } });
      const viewId = data?.workflow.saveTableParamViewInput.value?.id ?? null;

      onViewSaved(viewId);
      // we don't want to reset the currently loaded genome from the group in case the groupId stays the same (so no re-render)
      applySelectionContext(selectionMode, selectionTarget, { preserveGenome });
      setRestrictionVariantGenomesIds(nextRestrictions ?? []);
      setSelectedVariants(nextSelectedIds);
      setViewRowFilter(rowFilter);
    },
    [applySelectionContext, isDisabled, onViewSaved, restrictionRegion, saveVariantsView, workflowEventId],
  );

  const handleVariantGenomesSelection = (variantGenomeIds: number[]) => {
    if (isDisabled) {
      return;
    }

    setVariantSelectionError(null);
    persistVariantsSelection({
      genomeId: activeGenomeId,
      groupId: selectedGroupId,
      restrictionVariantGenomesIds: variantGenomeIds,
      selectedVariantsIds: selectedVariants ?? [],
      isOrganismTable,
      allowEmptySelection: true,
      rowFilter: viewRowFilter,
    }).catch((error) => {
      const message = error instanceof Error ? error.message : "Unable to save variant genomes";
      setVariantSelectionError(message);
    });
  };

  const isVariantGenomeSelectionDisabled =
    noGenomesAvailable ||
    isLoadingView ||
    isLoadingGroupGenomeId ||
    activeGenomeId == null ||
    isSavingVariantGenomes ||
    isDisabled;

  // in the playbook execution, we have some restrictions in the display which can be compensated by specific messages
  const contig = useSelector<IAppState, Contig | undefined>((state) => state.genomeBrowser.contig);
  const reportRangeSelectionLimitApplies = useSelector<IAppState, boolean | undefined>(
    (state) => state.nfPlaybookExecution.reportRangeSelectionLimitApplies,
  );
  let message: string | null = null;
  if (isPlaybookExecutionContext && isOrganismTable && !selectedVariants?.length) {
    if (restrictionRegion && reportRangeSelectionLimitApplies) {
      const regionLabel = contig
        ? `(${contig.sequenceId} [${restrictionRegion.range?.start}-${restrictionRegion.range?.stop}])`
        : ``;
      message = `Region ${regionLabel}`;
    } else {
      message = "All visible rows";
    }
  }

  return (
    <>
      <VStack
        className={formStyle.input_layout}
        data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}multi-select__button`}
      >
        {!!label && (
          <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
            <Label>{label}</Label>
            <Spacer />
            {optional && <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />}
          </HStack>
        )}
        <Description
          data-testid="variants-selection-description"
          className={clsx(formStyle.input_layout_column1, {
            [formStyle.description_with_tag]: label != undefined && optional,
          })}
        >
          {description}
        </Description>
        <div className={formStyle.input_layout_column1}>
          <VariantsTile
            selectedGenomeId={activeGenomeId}
            restrictionVariantGenomesIds={restrictionVariantGenomesIds}
            selectedVariantsCount={selectedVariants?.length}
            isLoadingView={isLoadingView || isLoadingGroupGenomeId}
            message={message}
          />
        </div>
        {(error || noGenomesAvailable) && (
          <FormError data-testid={"variants-selection-field-error"}>
            {noGenomesAvailable ? "Please select a genome first" : (error?.message ?? null)}
          </FormError>
        )}
        {isPlaybookExecutionContext ? (
          <HStack className={formStyle.input_layout_column1} style={{ width: "100%", paddingTop: "3px" }} wrap>
            {isVariantGenomeSelectionDisabled ? (
              <ActionButton label={"Select Variant Genomes"} isDisabled style={{ flex: "1 0 auto" }} />
            ) : (
              <VariantMutantGenomesSelectionActionButton
                onSave={handleVariantGenomesSelection}
                genomeId={activeGenomeId}
                restrictionVariantGenomesIds={restrictionVariantGenomesIds ?? []}
                style={{ flex: "1 0 auto" }}
              />
            )}
          </HStack>
        ) : (
          <HStack className={formStyle.input_layout_column1} style={{ width: "100%", paddingTop: "3px" }} wrap>
            <ActionButton
              label={"Select Variants"}
              isDisabled={
                (noGenomesAvailable && isOrganismTable) || isLoadingView || isLoadingGroupGenomeId || isDisabled
              }
              onPress={() => {
                setShowModal(true);
              }}
              style={{ flex: "1 0 auto" }}
            />
          </HStack>
        )}
        {isPlaybookExecutionContext && variantSelectionError && (
          <FormError data-testid="variant-selection-error">{variantSelectionError}</FormError>
        )}
        {!!extraAction && <HStack className={formStyle.input_layout_column2}>{extraAction}</HStack>}
      </VStack>
      {!isPlaybookExecutionContext && (
        <VariantsModal
          isOpen={!isDisabled && showModal}
          onClose={() => setShowModal(false)}
          defaultValue={{
            genomeId: selectedGenomeId,
            groupId: selectedGroupId,
            restrictionVariantGenomesIds: restrictionVariantGenomesIds,
            selectedVariantsIds: selectedVariants ?? [],
            isOrganismTable: isOrganismTable,
            restrictionRegion: restrictionRegion,
            rowFilter: viewRowFilter,
          }}
          availableGenomesIds={availableGenomesIds}
          onSaveSelection={(options) =>
            persistVariantsSelection({
              ...options,
              allowEmptySelection: true,
            })
          }
        />
      )}
    </>
  );
};
